import { useMemo, useState } from "react";
import { IndianRupee, Coins, Recycle, Factory, Flame, Layers, Scissors, Wrench, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/Bits";
import { useLocalStorage } from "@/lib/useLocalStorage";

const rupee = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (v) => Number(v) || 0;

function MoneyField({ label, value, onChange, testid, hint, Icon, disabled }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-500" />} {label}
      </span>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm font-bold text-slate-500">₹</span>
        <Input
          inputMode="decimal"
          data-testid={testid}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="h-12 rounded-xl border-slate-700 bg-slate-900/60 pl-7 text-right font-mono text-base font-bold text-white focus-visible:ring-orange-500/60 disabled:opacity-40"
        />
      </div>
      {hint && <span className="mt-1 block text-[10px] text-slate-500">{hint}</span>}
    </label>
  );
}

function FactorField({ label, value, onChange, testid, hint, disabled }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <div className="relative">
        <Input
          inputMode="decimal"
          data-testid={testid}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="h-10 rounded-lg border-slate-700 bg-slate-900/60 pr-9 text-right font-mono text-sm font-bold text-orange-200 focus-visible:ring-orange-500/60 disabled:opacity-40"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-500">kg</span>
      </div>
      {hint && <span className="mt-1 block text-[10px] text-slate-500">{hint}</span>}
    </label>
  );
}

function BreakRow({ label, value, sign, strong }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className={strong ? "font-semibold text-slate-200" : "text-slate-400"}>{label}</span>
      <span className={`font-mono ${sign === "-" ? "text-red-400" : strong ? "font-bold text-white" : "text-slate-200"}`}>
        {sign === "-" ? "−" : sign === "+" ? "+" : ""}{rupee(value)}
      </span>
    </div>
  );
}

function CostCard({ title, badge, tag, children, final, testid, open, onToggle }) {
  return (
    <Card className="overflow-hidden p-0" data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 border-b border-slate-800 bg-slate-800/40 px-4 py-3 text-left transition-colors hover:bg-slate-800/70"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500/15 text-orange-300">{badge}</span>
          <div>
            <div className="font-display text-base font-extrabold text-white">{title}</div>
            {tag && <div className="text-[10px] uppercase tracking-wider text-slate-500">{tag}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[9px] font-bold uppercase tracking-wider text-orange-200/80">/ 50 kg bag</div>
            <div data-testid={`${testid}-final`} className="font-mono text-lg font-black text-orange-400">{rupee(final)}</div>
          </div>
          <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 py-3 animate-fade-up">
          {children}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="flex flex-col rounded-xl bg-slate-800/50 px-4 py-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Cost / kg</span>
              <span data-testid={`${testid}-perkg`} className="font-mono text-lg font-black text-orange-300">{rupee((Number(final) || 0) / 50)}</span>
            </div>
            <div className="flex flex-col rounded-xl bg-orange-500/10 px-4 py-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-orange-200">Cost / 50 kg Bag</span>
              <span className="font-mono text-xl font-black text-orange-400">{rupee(final)}</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

const DEFAULTS = {
  mode: "inhouse", // inhouse | jobwork
  // Shared wire-rod & scrap RATE for NUT + BOLT 4.6
  sharedWireRod: "", sharedScrap: "",
  // NUT (own drawing, mfg & factors)
  nutDraw: "", nutMfg: "", nutWireF: "62", nutScrapF: "12",
  // BOLT 4.6 (own drawing, mfg & factors)
  b46Draw: "", b46Mfg: "", b46WireF: "55", b46ScrapF: "5",
  // BOLT 8.8 (own rates)
  b88WireRod: "", b88Draw: "", b88Scrap: "", b88Mfg: "", b88WireF: "55", b88ScrapF: "5",
  b88Tempering: "", b88Finish: "zinc", b88FinishCharge: "", b88Misc: "",
};

export function CostingPage() {
  const [stored, setStored] = useLocalStorage("trbolt_costing", DEFAULTS);
  const inp = { ...DEFAULTS, ...stored };
  const set = (k, v) => setStored({ ...inp, [k]: v });
  const isJobwork = inp.mode === "jobwork";
  const [openCard, setOpenCard] = useState(null);
  const toggle = (id) => setOpenCard((cur) => (cur === id ? null : id));

  const c = useMemo(() => {
    const calc = (wr, draw, sc, mfg, wf, sf) => {
      const factor = num(wf);
      const wire = num(wr) * factor;
      const drawing = num(draw) * factor;
      // Jobwork: material supplied by customer → no scrap deduction
      const effSf = isJobwork ? 0 : num(sf);
      const scrap = num(sc) * effSf;
      const net = wire + drawing - scrap;
      return { wire, drawing, scrap, net, effSf, base: net + num(mfg) };
    };

    const nut = calc(inp.sharedWireRod, inp.nutDraw, inp.sharedScrap, inp.nutMfg, inp.nutWireF, inp.nutScrapF);
    const b46 = calc(inp.sharedWireRod, inp.b46Draw, inp.sharedScrap, inp.b46Mfg, inp.b46WireF, inp.b46ScrapF);
    const b88 = calc(inp.b88WireRod, inp.b88Draw, inp.b88Scrap, inp.b88Mfg, inp.b88WireF, inp.b88ScrapF);
    const temper = num(inp.b88Tempering);
    const finishCharge = num(inp.b88FinishCharge);
    const misc = num(inp.b88Misc);

    return {
      nut: { ...nut, final: nut.base, mfg: num(inp.nutMfg), wf: num(inp.nutWireF) },
      b46: { ...b46, final: b46.base, mfg: num(inp.b46Mfg), wf: num(inp.b46WireF) },
      b88: { ...b88, final: b88.base + temper + finishCharge + misc, mfg: num(inp.b88Mfg), wf: num(inp.b88WireF), temper, finishCharge, misc },
    };
  }, [inp, isJobwork]);

  const nutCard = (
      <CostCard title="NUT" tag="Shared rate · own drawing, mfg & scrap ratio" badge={<IndianRupee className="h-4 w-4" />} final={c.nut.final} testid="card-nut" open={openCard === "nut"} onToggle={() => toggle("nut")}>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <MoneyField label="Wire Drawing Charges" hint="per kg" Icon={Wrench} testid="cost-nut-draw" value={inp.nutDraw} onChange={(v) => set("nutDraw", v)} />
          <MoneyField label="Manufacturing Cost" hint="per 50 kg bag" Icon={Factory} testid="cost-nut-mfg" value={inp.nutMfg} onChange={(v) => set("nutMfg", v)} />
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <FactorField label="Wire Rod Factor" hint="default 62 kg" testid="factor-nut-wire" value={inp.nutWireF} onChange={(v) => set("nutWireF", v)} />
          <FactorField label="Scrap Factor" hint={isJobwork ? "0 in jobwork" : "default 12 kg"} testid="factor-nut-scrap" value={isJobwork ? "0" : inp.nutScrapF} onChange={(v) => set("nutScrapF", v)} disabled={isJobwork} />
        </div>
        <BreakRow label={`Wire Rod Cost (rate × ${c.nut.wf})`} value={c.nut.wire} sign="+" />
        <BreakRow label={`Wire Drawing (charge × ${c.nut.wf})`} value={c.nut.drawing} sign="+" />
        <BreakRow label={`Scrap Deduction (scrap × ${c.nut.effSf})`} value={c.nut.scrap} sign="-" />
        <BreakRow label="Net Wire Rod Cost" value={c.nut.net} strong />
        <BreakRow label="Manufacturing Cost" value={c.nut.mfg} sign="+" />
      </CostCard>
  );

  const bolt46Card = (
      <CostCard title="BOLT 4.6" tag="Shared rate · own drawing, mfg & scrap ratio" badge={<IndianRupee className="h-4 w-4" />} final={c.b46.final} testid="card-bolt46" open={openCard === "b46"} onToggle={() => toggle("b46")}>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <MoneyField label="Wire Drawing Charges" hint="per kg" Icon={Wrench} testid="cost-bolt46-draw" value={inp.b46Draw} onChange={(v) => set("b46Draw", v)} />
          <MoneyField label="Manufacturing Cost" hint="per 50 kg bag" Icon={Factory} testid="cost-bolt46-mfg" value={inp.b46Mfg} onChange={(v) => set("b46Mfg", v)} />
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <FactorField label="Wire Rod Factor" hint="default 55 kg" testid="factor-bolt46-wire" value={inp.b46WireF} onChange={(v) => set("b46WireF", v)} />
          <FactorField label="Scrap Factor" hint={isJobwork ? "0 in jobwork" : "default 5 kg"} testid="factor-bolt46-scrap" value={isJobwork ? "0" : inp.b46ScrapF} onChange={(v) => set("b46ScrapF", v)} disabled={isJobwork} />
        </div>
        <BreakRow label={`Wire Rod Cost (rate × ${c.b46.wf})`} value={c.b46.wire} sign="+" />
        <BreakRow label={`Wire Drawing (charge × ${c.b46.wf})`} value={c.b46.drawing} sign="+" />
        <BreakRow label={`Scrap Deduction (scrap × ${c.b46.effSf})`} value={c.b46.scrap} sign="-" />
        <BreakRow label="Net Wire Rod Cost" value={c.b46.net} strong />
        <BreakRow label="Manufacturing Cost" value={c.b46.mfg} sign="+" />
      </CostCard>
  );

  const bolt88Card = (
      <CostCard title="BOLT 8.8" tag={isJobwork ? "Jobwork · tempered + finish" : "In-house · tempered + finish"} badge={<Flame className="h-4 w-4" />} final={c.b88.final} testid="card-bolt88" open={openCard === "b88"} onToggle={() => toggle("b88")}>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MoneyField label="Wire Rod Rate" hint="per kg" Icon={Coins} testid="cost-bolt88-wire-rod" value={inp.b88WireRod} onChange={(v) => set("b88WireRod", v)} />
          <MoneyField label="Wire Drawing Charges" hint="per kg" Icon={Wrench} testid="cost-bolt88-draw" value={inp.b88Draw} onChange={(v) => set("b88Draw", v)} />
          <MoneyField label="Scrap Rate" hint="per kg" Icon={Recycle} testid="cost-bolt88-scrap" value={inp.b88Scrap} onChange={(v) => set("b88Scrap", v)} disabled={isJobwork} />
          <MoneyField label="Manufacturing Cost" hint="per 50 kg bag" Icon={Factory} testid="cost-bolt88-mfg" value={inp.b88Mfg} onChange={(v) => set("b88Mfg", v)} />
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
          <FactorField label="Wire Rod Factor" hint="default 55 kg" testid="factor-bolt88-wire" value={inp.b88WireF} onChange={(v) => set("b88WireF", v)} />
          <FactorField label="Scrap Factor" hint={isJobwork ? "0 in jobwork" : "default 5 kg"} testid="factor-bolt88-scrap" value={isJobwork ? "0" : inp.b88ScrapF} onChange={(v) => set("b88ScrapF", v)} disabled={isJobwork} />
        </div>
        <div className="mb-3 grid grid-cols-1 gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <MoneyField label="Tempering Charges" hint="per 50 kg bag" Icon={Flame} testid="cost-bolt88-tempering" value={inp.b88Tempering} onChange={(v) => set("b88Tempering", v)} />
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              <Layers className="h-3.5 w-3.5 text-slate-500" /> Finish Type
            </span>
            <Select value={inp.b88Finish} onValueChange={(v) => set("b88Finish", v)}>
              <SelectTrigger data-testid="cost-bolt88-finish" className="h-12 rounded-xl border-slate-700 bg-slate-900/60 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                <SelectItem value="zinc" data-testid="cost-bolt88-finish-zinc">Zinc</SelectItem>
                <SelectItem value="autoblack" data-testid="cost-bolt88-finish-autoblack">Auto Black</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <MoneyField label="Finish Charges" hint="per 50 kg bag" testid="cost-bolt88-finish-charge" value={inp.b88FinishCharge} onChange={(v) => set("b88FinishCharge", v)} />
          <MoneyField label="Misc Charges" hint="per 50 kg bag" Icon={Scissors} testid="cost-bolt88-misc" value={inp.b88Misc} onChange={(v) => set("b88Misc", v)} />
        </div>
        <BreakRow label={`Wire Rod Cost (rate × ${c.b88.wf})`} value={c.b88.wire} sign="+" />
        <BreakRow label={`Wire Drawing (charge × ${c.b88.wf})`} value={c.b88.drawing} sign="+" />
        <BreakRow label={`Scrap Deduction (scrap × ${c.b88.effSf})`} value={c.b88.scrap} sign="-" />
        <BreakRow label="Net Wire Rod Cost" value={c.b88.net} strong />
        <BreakRow label="Manufacturing Cost" value={c.b88.mfg} sign="+" />
        <BreakRow label="Tempering Charges" value={c.b88.temper} sign="+" />
        <BreakRow label={`${inp.b88Finish === "zinc" ? "Zinc" : "Auto Black"} Finish Charges`} value={c.b88.finishCharge} sign="+" />
        <BreakRow label="Misc Charges" value={c.b88.misc} sign="+" />
      </CostCard>
  );


  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-white">Costing</h1>
        <p className="text-sm text-slate-500">Cost per 50 kg finished bag · separate rates per product · updates live</p>
      </div>

      {/* Mode selector */}
      <Card>
        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <Layers className="h-3.5 w-3.5 text-slate-500" /> Costing Type
          </span>
          <Select value={inp.mode} onValueChange={(v) => set("mode", v)}>
            <SelectTrigger data-testid="cost-mode" className="h-12 rounded-xl border-slate-700 bg-slate-900/60 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              <SelectItem value="inhouse" data-testid="cost-mode-inhouse">In-House</SelectItem>
              <SelectItem value="jobwork" data-testid="cost-mode-jobwork">Jobwork</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <p className="mt-2 text-[11px] text-slate-500">
          {isJobwork
            ? "Jobwork: material supplied by customer — scrap factor is set to 0 (no scrap deduction)."
            : "In-House: scrap factor is active and deducted from wire-rod cost."}
        </p>
      </Card>

      {/* Shared rates for NUT + BOLT 4.6 */}
      <Card>
        <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <Coins className="h-3.5 w-3.5 text-slate-500" /> Shared Rate — NUT &amp; BOLT 4.6
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <MoneyField label="Wire Rod Rate" hint="per kg · used by Nut & Bolt 4.6" Icon={Coins} testid="cost-shared-wire-rod" value={inp.sharedWireRod} onChange={(v) => set("sharedWireRod", v)} />
          <MoneyField label="Scrap Rate" hint="per kg · used by Nut & Bolt 4.6" Icon={Recycle} testid="cost-shared-scrap" value={inp.sharedScrap} onChange={(v) => set("sharedScrap", v)} disabled={isJobwork} />
        </div>
      </Card>

      {/* Product cards — Jobwork hides NUT and shows 8.8 first */}
      {isJobwork ? (
        <>
          {bolt88Card}
          {bolt46Card}
        </>
      ) : (
        <>
          {nutCard}
          {bolt46Card}
          {bolt88Card}
        </>
      )}

      <p className="pb-2 text-center text-[11px] text-slate-600">All costs are per 50 kg finished bag. Each product has its own rates & editable factors.</p>
    </div>
  );
}
